import { useEffect, useId, useState, type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { invalidDateError } from "@/utils/dateUtils";

interface CampoEditavelProps {
  label: string;
  valor: string;
  onSalvar: (valor: string) => void;
  tipo?: "text" | "date" | "email";
  disabled?: boolean;
  placeholder?: string;
  sufixo?: ReactNode;
  ajuda?: ReactNode;
  destaque?: boolean;
  inputMode?: "decimal" | "numeric" | "text";
  className?: string;
  inputClassName?: string;
  id?: string;
  normalizar?: (valor: string) => string;
  onRascunhoChange?: (valor: string) => void;
  multilinha?: boolean;
}

/**
 * Input com rascunho local que só grava no blur (ou Enter) e só quando o valor
 * mudou. Enquanto o campo está em foco, respostas do servidor não sobrescrevem
 * o que está sendo digitado. Com `multilinha`, Enter quebra a linha e
 * Cmd+Enter (ou Ctrl+Enter) grava. Com `tipo="date"`, um ano fora de 2000 a
 * 2099 não grava e o campo fica marcado como inválido, inclusive quando o
 * valor salvo já veio assim.
 */
export function CampoEditavel({
  label,
  valor,
  onSalvar,
  tipo = "text",
  disabled,
  placeholder,
  sufixo,
  ajuda,
  destaque,
  inputMode,
  className,
  inputClassName,
  id,
  normalizar,
  onRascunhoChange,
  multilinha,
}: CampoEditavelProps) {
  const idGerado = useId();
  const inputId = id ?? idGerado;
  const [rascunho, setRascunho] = useState(valor);
  const [emEdicao, setEmEdicao] = useState(false);
  const [erroDigitado, setErroDigitado] = useState<string | null>(null);
  const erro = tipo === "date" ? (erroDigitado ?? invalidDateError(valor)) : null;

  useEffect(() => {
    if (!emEdicao) setRascunho(valor);
  }, [valor, emEdicao]);

  const confirmar = () => {
    setEmEdicao(false);
    const final = normalizar ? normalizar(rascunho) : rascunho;
    if (final !== rascunho) setRascunho(final);
    if (tipo === "date") {
      const erroDaData = invalidDateError(final.trim());
      setErroDigitado(erroDaData);
      if (erroDaData) return;
    }
    if (final.trim() !== valor.trim()) onSalvar(final.trim());
  };

  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={inputId} className="text-xs font-medium text-muted-foreground">
        {label}
      </Label>
      <div className="relative">
        {multilinha ? (
          <Textarea
            id={inputId}
            value={rascunho}
            disabled={disabled}
            placeholder={placeholder}
            rows={3}
            onFocus={() => setEmEdicao(true)}
            onChange={(event) => {
              setRascunho(event.target.value);
              onRascunhoChange?.(event.target.value);
            }}
            onBlur={confirmar}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) event.currentTarget.blur();
              if (event.key === "Escape") {
                setRascunho(valor);
                setEmEdicao(false);
                event.currentTarget.blur();
              }
            }}
            className={cn(
              destaque && "border-amber-400 bg-amber-50/60 focus-visible:ring-amber-400",
              inputClassName,
            )}
          />
        ) : (
          <Input
            id={inputId}
            type={tipo}
            value={rascunho}
            disabled={disabled}
            placeholder={placeholder}
            inputMode={inputMode}
            onFocus={() => setEmEdicao(true)}
            onChange={(event) => {
              setRascunho(event.target.value);
              onRascunhoChange?.(event.target.value);
            }}
            onBlur={confirmar}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.currentTarget.blur();
              if (event.key === "Escape") {
                setRascunho(valor);
                setErroDigitado(null);
                setEmEdicao(false);
                event.currentTarget.blur();
              }
            }}
            aria-invalid={!!erro || undefined}
            className={cn(
              sufixo && "pr-9",
              destaque && "border-amber-400 bg-amber-50/60 focus-visible:ring-amber-400",
              erro && "border-tone-danger-fg focus-visible:ring-tone-danger-fg/30",
              inputClassName,
            )}
          />
        )}
        {sufixo && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
            {sufixo}
          </span>
        )}
      </div>
      {erro && <p role="alert" className="text-[11px] leading-snug text-tone-danger-fg">{erro}</p>}
      {ajuda && <div className="text-[11px] leading-snug text-muted-foreground">{ajuda}</div>}
    </div>
  );
}
